import { getYaml } from '@shell/components/Drawer/ResourceDetailDrawer/helpers';
import { ConfigProps, YamlProps } from '@shell/components/Drawer/ResourceDetailDrawer/types';
import { inject, provide } from 'vue';
import { useStore } from 'vuex';

export async function useDefaultYamlTabProps(resource: any): Promise<YamlProps | undefined> {
  try {
    // Use the resource's own instance-level canYaml (hasLink('view')) rather than the type-map's
    // static canYaml option: that option's meaning varies by type/author intent and isn't a
    // reliable "can this type ever produce real yaml" signal - e.g. ext.cattle.io.kubeconfig sets
    // canYaml: false purely to hide an unrelated button, while still genuinely supporting yaml
    // (it has a real `view` link). The instance getter mirrors exactly what getYaml() itself
    // checks, so it accurately reflects whether fetching yaml here will actually produce content.
    if (!resource.canYaml) {
      return;
    }

    const yaml = await getYaml(resource);

    return {
      resource,
      yaml
    };
  } catch (e) {
    console.warn(`Could not determine/fetch yaml for '${ resource?.type }'`, e); // eslint-disable-line no-console
  }
}

export function useDefaultConfigTabProps(resource: any): ConfigProps | undefined {
  const store = useStore();

  // You don't want to show the Config tab if there isn't a an edit page to show and you don't want to show it if there isn't
  // a detail page because we default to showing the existing edit page if the detail page doesn't exist. Showing them again
  // wouldn't be worth while.
  if (!store.getters['type-map/hasCustomEdit'](resource.type) || !store.getters['type-map/hasCustomDetail'](resource.type) || resource.disableResourceDetailDrawerConfigTab) {
    return;
  }

  return {
    resource,
    component:    store.getters['type-map/importEdit'](resource.type),
    resourceType: resource.type
  };
}

const IS_IN_RESOURCE_DETAIL_DRAWER_KEY = 'isInResourceDetailDrawerKey';

/**
 * Used to add a provide method which will indicate to all ancestors that they're inside the ResourceDetailDrawer. This is useful because we show
 * config page components both independently and within the ResourceDetailDrawer and we sometimes want to distinguish between the two use cases.
*/
export function useResourceDetailDrawerProvider() {
  provide(IS_IN_RESOURCE_DETAIL_DRAWER_KEY, true);
}

/**
 * A composable used to determine if the current component was instantiated as an ancestor of a ResourceDetailDrawer.
 * @returns true if the component is an ancestor of ResourceDetailDrawer, otherwise false
 */
export function useIsInResourceDetailDrawer() {
  return inject(IS_IN_RESOURCE_DETAIL_DRAWER_KEY, false);
}
